<script setup>

import {computed, onMounted, ref, watch} from "vue";
import CopyableText from "@/components/copyableText.vue";

const props = defineProps({
    courseId: {
        type: Number,
        required: true
    }
});

const course = ref(null);
const savingState = ref('')
const editingSettingsChapter = ref(null)
const originalAvailableFromWhenEditing = ref(null)
const creatingInvoiceLink = ref(false)
const activeTab = ref('general')

const loadCourse = async () => {
    const res = await axios.get(route('api.course.backoffice.edit', props.courseId));
    course.value = res.data.course
}

const updateCourse = async () => {
    savingState.value = 'waiting'
    const res = await axios.put(route('api.course.backoffice.update', props.courseId), course.value);
    course.value = res.data.course
    if(res.data.success) {
        savingState.value = 'success'
    } else {
        savingState.value = 'error'
    }
}

const normalizeAvailableFrom = (value) => {
    if (!value) {
        return null
    }

    return String(value).split('T')[0]
}

const openChapterSettings = (chapter) => {
    originalAvailableFromWhenEditing.value = normalizeAvailableFrom(chapter.available_from)
    editingSettingsChapter.value = chapter
}

const closeChapterSettings = () => {
    editingSettingsChapter.value = null
    originalAvailableFromWhenEditing.value = null
}

const updateFromEdit = async (_chapter) => {
    const availableFromChanged = originalAvailableFromWhenEditing.value !== normalizeAvailableFrom(_chapter.available_from)

    if (editingSettingsChapter.value) {
        editingSettingsChapter.value.available_from = _chapter.available_from
        editingSettingsChapter.value.available_until = _chapter.available_until
        editingSettingsChapter.value.title = _chapter.title
        editingSettingsChapter.value.description = _chapter.description
        editingSettingsChapter.value.published = _chapter.published
        editingSettingsChapter.value.course_chapter_group_id = _chapter.course_chapter_group_id
    }

    closeChapterSettings()

    if (availableFromChanged) {
        await loadCourse()
    }
}

const paymentLinkCreated = () => {
    creatingInvoiceLink.value = false
    loadCourse()
}

const courseValidates = computed(() => {
    return course.value.title.length > 0
})

const paymentLinkProducts = computed(() => {
    return [{
        title: course.value.title,
        price: 0,
        quantity: 1,
        description: course.value.description,
        linkableId: course.value.id,
        linkableType: 'course',
        quantity_is_fixed: false,
    }]
})

watch(() => course.value?.requires_license, (newVal) => {
    if (newVal === false) {
        course.value.license_holders_only_see_new_chapters = false
    }
})

onMounted(() => {
    loadCourse();
})

</script>
<template>
    <div>
        <div v-if="course">
            <div class="md:sticky top-2 z-topbar">
                <frosted-bar>
                    <template #navigation>
                        <a :href="route('course.backoffice.index')+'#'+course.type">
                            {{ _mft('course:domainNamePlural') }}
                        </a>
                    </template>

                    <div class="lg:flex items-center justify-between lg:space-x-4">
                        <h1>{{ course.title }}</h1>

                        <div class="flex justify-end items-center space-x-4 mt-1 lg:mg-0">
                            <story-lane-modal :module="'content-v2'"></story-lane-modal>

                            <button-secondary
                                @click="creatingInvoiceLink = true"
                            >
                                {{ _mft('course:createInvoiceLink') }}
                            </button-secondary>

                            <button-base
                                :style-type="courseValidates ? 'success' : 'disabled'"
                                :disabled="! courseValidates"
                                @click="updateCourse()"
                                :state="savingState"
                            >
                                {{ _mft('shared:action.save') }}
                            </button-base>
                        </div>
                    </div>
                </frosted-bar>
            </div>

            <card-tabbed class="mt-4" @changestatustab="activeTab = $event">
                <card-tabbed-tab value="general" :title="_mft('course:tab.general.title')" :active="true">
                    <input-text
                        v-model:content="course.title"
                        :label="_mft('course:input.title')"
                    ></input-text>

                    <input-provider
                        :label="_mft('course:input.provider')"
                        name="provider_id"
                        v-model="course.provider.id"
                    />

                    <card-sub class="mb-4">
                        <toggle-switch
                            v-if="course.type === 'class'"
                            v-model:content="course.chapters_must_be_consumed_in_order"
                            :label="_mft('course:setting.forcedChapterOrder.label')"
                        />

                        <toggle-switch
                            v-if="course.type === 'feed'"
                            class="mt-4"
                            v-model:content="course.requires_license"
                            :label="_mft('course:setting.requiresLicense.label')"
                        />
                        <p
                            v-if="course.type === 'feed'"
                            class="text-xs italic -mt-3"
                        >
                            {{
                                course.requires_license
                                    ? _mft('course:setting.requiresLicense.descriptionEnabled')
                                    : _mft('course:setting.requiresLicense.descriptionDisabled')
                            }}
                        </p>

                        <div
                            class="mt-4"
                            v-if="course.requires_license && course.type === 'feed'"
                        >
                            <toggle-switch
                                v-model:content="course.license_holders_only_see_new_chapters"
                                :label="_mft('course:setting.licenseVisibility.label')"
                            />
                            <p class="text-xs italic -mt-3">{{ _mft('course:setting.licenseVisibility.description', {name: course.title }) }}</p>
                        </div>
                    </card-sub>

                    <input-editor
                        v-model:content="course.description"
                        :label="_mft('course:input.description')"
                    ></input-editor>
                </card-tabbed-tab>

                <card-tabbed-tab value="chapters" :title="course.type === 'class' ? _mft('course:tab.chapters.title') : _mft('course:tab.posts.title')">
                    <div
                        v-if="course.type === 'class'"
                        class="mb-4 lg:mb-8"
                    >
                        <div>
                            <course-chapter-orderable-list
                                :chapters="course.chapters"
                                :chapter-groups="course.chapter_groups ?? []"
                                :course-id="course.id"
                                @edit-settings="openChapterSettings"
                                @delete:chapter="loadCourse"
                                @changed="loadCourse"
                            ></course-chapter-orderable-list>
                        </div>
                    </div>

                    <course-feed-post-list
                        v-if="course.type === 'feed'"
                        :chapters="course.chapters"
                        :course-id="course.id"
                        @edit-settings="openChapterSettings"
                        @delete:chapter="loadCourse"
                    ></course-feed-post-list>

                </card-tabbed-tab>
                <card-tabbed-tab
                    value="users"
                    :title="_mft('course:tab.participants.title')"
                >
                    <course-participants
                        v-if="activeTab === 'users'"
                        :course="course"
                    ></course-participants>
                </card-tabbed-tab>
                <card-tabbed-tab
                    v-if="course.invoice_links?.length > 0"
                    value="paymentLinks"
                    :title="_mft('course:tab.paymentMethods.title')"
                >
                    <ul class="divide-y">
                        <li
                            v-for="link in course.invoice_links"
                            :key="link.id"
                            class="flex justify-between items-center py-1"
                        >
                            <span>
                                {{ link.title }}
                            </span>
                            <div class="flex items-center space-x-2">
                                <copyable-text
                                    v-if="link.type === 'single-use'"
                                    :copyable-text="siteRoute('invoicing.link.show', {uuid: link.uuid})"
                                    :show-url="false"
                                    class="mr-2"
                                />
                                <a
                                    :href="route('invoice-links.edit', link.uuid)"
                                    class="mf-link"
                                    target="_blank"
                                >
                                    {{ _mft('shared:action.edit') }}
                                </a>
                            </div>
                        </li>
                    </ul>
                </card-tabbed-tab>
            </card-tabbed>
        </div>
        <mf-spinner-medium v-else class="w-12 h-6" />

        <course-chapter-settings
            v-if="editingSettingsChapter"
            :course-type="course.type"
            :chapter="editingSettingsChapter"
            @update:chapter="updateFromEdit"
            @close="closeChapterSettings"
        />

        <slideout-with-slots
            v-if="creatingInvoiceLink"
            @close="creatingInvoiceLink = false"
            max-width-class="max-w-xl"
        >
            <template #title>
                <h2>{{ _mft('course:createInvoiceLink') }}</h2>
            </template>
            <template #body>
                <invoice-link-form
                    :preset-products="paymentLinkProducts"
                    :preset-title="course.title"
                    :redirect="false"
                    form-style="ui"
                    :show-title="false"
                    :hidden-features="['backlink', 'navigation']"
                    preselected-tab="settings"
                    class="container"
                    @payment-link-created="paymentLinkCreated"
                ></invoice-link-form>
            </template>
        </slideout-with-slots>

    </div>
</template>
